import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { LayoutGrid, Eye, EyeOff } from "lucide-react";
import { authAPI } from "../../services/api"; // adjust path based on actual file structure


const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [firstUserId, setFirstUserId] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please enter both email and password",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const user = await login(email, password); // Get full user object


      if (user.is_first_login) {
        setFirstUserId(user._id); // backend expects _id
        setShowPasswordReset(true);
        toast({
          title: "First-time login",
          description: "Please reset your password",
        });
        return;
      }


      const role = localStorage.getItem("role");
      if (role === "master_admin") {
        navigate("/master-admin");
      } else if (role === "company_super_admin") {
        navigate("/super-admin");
      } else if (role === "company_admin") {
        navigate("/admin");
      } else {
        navigate("/login");
      }

    } catch (error) {
      toast({
        title: "Login failed",
        description: error.response?.data?.message || "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast({
        title: "Error",
        description: "Please enter a new password",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await authAPI.resetFirstTimePassword(firstUserId, newPassword);
      toast({
        title: "Success",
        description: "Password reset! Please login again.",
      });
      setShowPasswordReset(false);
      setPassword("");
      setNewPassword("");
      navigate("/login");
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Could not reset password",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleNewPasswordVisibility = () => {
  setShowNewPassword(!showNewPassword);
};


  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header Section - Outside the form */}
      <div className="flex items-center justify-center pt-4 pb-4 flex-shrink-0">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="flex items-center">
            <LayoutGrid className="h-8 w-8 text-primary" />
            <h1 className="ml-2 text-2xl font-bold">AssessPro</h1>
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
            Login
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {showPasswordReset
              ? "Reset your password to proceed."
              : "Sign in to access the admin dashboard"}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 min-h-0">
        <div className="w-full max-w-6xl flex items-center justify-center h-full">
          <Card className="w-full h-full max-h-full flex flex-row overflow-hidden shadow-2xl">
            {/* Left side - Image Section (50% width) */}
            <div className="w-full lg:w-1/2 min-h-64 lg:min-h-full hidden lg:block bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-6">
              <div className="text-center w-full h-full flex items-center justify-center">
                <img
                  src="https://cdni.iconscout.com/illustration/premium/thumb/user-login-illustration-download-in-svg-png-gif-file-formats--password-profile-businessman-pack-business-illustrations-5857593.png"
                  alt="Assessment Image"
                  className="w-full h-full max-h-96 lg:max-h-full object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>

            {/* Right side - Form Section (50% width) */}
            <div className="w-full lg:w-1/2 flex flex-col">
              <CardContent className="flex-1 flex flex-col justify-center py-8">
                {showPasswordReset ? (
                  <form onSubmit={handlePasswordUpdate}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">New Password</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={isSubmitting} >
                        {isSubmitting ? "Updating..." : "Update Password"}
                      </Button>
                    </div>
                  </form>

                ) : (
                  <form onSubmit={handleLogin}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="admin@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password">Password</Label>
                        </div>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={togglePasswordVisibility}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Signing in..."
                          : "Sign In"}
                      </Button>
                    </div>
                  </form>)}
              </CardContent>

              <CardFooter className="flex flex-col space-y-2 py-4 px-8 flex-shrink-0">
                {/* Home Redirect Button */}
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="w-full"
                >
                  Go to Home
                </Button>
              </CardFooter>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
